import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { PUBLIC_API_URL } from '@src/config';
import axios from 'axios';

// Async Thunk for fetching BOM by ID
export const fetchBOMById = createAsyncThunk(
    'bom/fetchBOMById',
    async (bomId, thunkAPI) => {
        try {
            const url = `${PUBLIC_API_URL}/api/boms/${bomId}`;
            const { data } = await axios.get(url);
            return data.data; // Map directly to the data object in the response
        } catch (error) {
            return thunkAPI.rejectWithValue({ error: error.message });
        }
    }
);

// Initial State
const initialState = {
    billNo: null,
    bomDetails: {
        productCode: '',
        productDescription: '',
        productCategory: '',
        releaseDate: null,
        salesChannels: '',
        comments: ''
    },
    costDetails: {
        materialCost: 0,
        charges: 0,
        labour: 0,
        defaultMargin: 10,
        totalPrice: 0
    },
    currency: null, 
    table: [],
    selectedRows: new Set(),
    searchQuery: '',
    itemsLoaded: 0,
    loading: false,
    error: null,
    unsavedChanges: false
};

// BOM Slice
const bomSlice = createSlice({
    name: 'bom',
    initialState,
    reducers: {
        // BOM Details Reducers
        setBillNo: (state, action) => {
            state.billNo = action.payload;
            state.unsavedChanges = true;
        },
        setProductCode: (state, action) => {
            state.bomDetails.productCode = action.payload;
            state.unsavedChanges = true;
        },
        setProductDescription: (state, action) => {
            state.bomDetails.productDescription = action.payload;
            state.unsavedChanges = true;
        },
        setProductCategory: (state, action) => {
            state.bomDetails.productCategory = action.payload;
            state.unsavedChanges = true;
        },
        setReleaseDate: (state, action) => {
            state.bomDetails.releaseDate = action.payload;
            state.unsavedChanges = true;
        },
        setSalesChannels: (state, action) => {
            state.bomDetails.salesChannels = action.payload;
            state.unsavedChanges = true;
        },
        setComments: (state, action) => {
            state.bomDetails.comments = action.payload;
            state.unsavedChanges = true;
        },

        // Currency Reducer
        setCurrency: (state, action) => {
            state.currency = action.payload;
            state.unsavedChanges = true;
        },

        addLine: (state, action) => {
            state.table.push(action.payload);
            state.unsavedChanges = true;
        },
        removeLine: (state, action) => {
            state.table = state.table.filter((_, index) => index !== action.payload);
            state.unsavedChanges = true;
        },
        setTable: (state, action) => {
            state.table = action.payload;
            state.unsavedChanges = true;
        },

        // Selected Lines Reducers
        setSelectedRows: (state, action) => {
            state.selectedRows = action.payload;
        },

        // Search Term Reducer
        setSearchQuery: (state, action) => {
            state.searchQuery = action.payload;
        },

        // Cost Details Reducers
        setMaterialCost: (state, action) => {
            state.costDetails.materialCost = action.payload;
            state.unsavedChanges = true;
        },
        setCharges: (state, action) => {
            state.costDetails.charges = action.payload;
            state.unsavedChanges = true;
        },
        setDefaultMargin: (state, action) => {
            state.costDetails.defaultMargin = action.payload;
            state.unsavedChanges = true;
        },

        setLabour: (state, action) => {
            state.costDetails.labour = action.payload;
            state.unsavedChanges = true;
        },
        setItemsLoaded: (state, action) => {
            state.itemsLoaded = action.payload;
        },
        calculateTotalPrice: (state) => {
            state.costDetails.totalPrice =
                state.costDetails.materialCost + state.costDetails.charges + state.costDetails.labour;
        },

        // Reset BOM
        resetBOM: () => initialState
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchBOMById.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchBOMById.fulfilled, (state, action) => {
                state.loading = false;
                const bomData = action.payload;

                state.billNo = bomData.billNo || null;
                state.bomDetails = {
                    productCode: bomData.productCode || '',
                    productDescription: bomData.productDescription || '',
                    productCategory: bomData.productCategory || '',
                    releaseDate: new Date(bomData.releaseDate).toISOString().split('T')[0] || null,
                    salesChannels: bomData.salesChannels || '',
                    comments: bomData.comments || ''
                };
                state.costDetails = {
                    materialCost: bomData.materialCost || 0,
                    charges: bomData.charges || 0,
                    labour: bomData.labour || 0,
                    totalPrice: bomData.totalPrice || 0,
                    defaultMargin: bomData.defaultMargin || 10
                };
                state.table = bomData.table.map((item) => ({ ...item, expanded: false }));
                state.currency = bomData.currency || null; 
                state.unsavedChanges = false;
            })
            .addCase(fetchBOMById.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload.error;
            });
    }
});

// Actions
export const {
    setBillNo,
    setProductCode,
    setProductDescription,
    setProductCategory,
    setReleaseDate,
    setItemsLoaded,
    setSalesChannels,
    setComments,
    setCurrency,
    setTable,
    addLine,
    removeLine,
    setSelectedRows,
    setSearchQuery,
    setMaterialCost,
    setCharges,
    setDefaultMargin,
    setLabour,
    calculateTotalPrice,
    resetBOM
} = bomSlice.actions;

// Reducer
export default bomSlice.reducer;
